'use client';

import { imageRendition, ownedImageLoader } from '@lib/asset-renditions';
import type { ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';
import { styled, createGlobalStyle } from 'styled-components';
import cs from 'classnames';

import { isHostedAsset, resolveAssetReference } from '@lib/assets';
import type { ColorScheme, Gradient } from '@types';

type BaseSectionProps = {
  /**
   * Sets the global color scheme when the section is in view.
   */
  colorScheme?: ColorScheme;
  /**
   * Should the section include vertical padding?
   *
   * Note that this adds quite a bit of vertical space surrounding the modules
   * within, so should be used sparingly for standard sections.
   *
   * Note we recommend setting this to `true` whenever using the for the
   * `split` variant at the top of the page, to provide some breathing room
   * below the navigation.
   *
   * The amount of padding depends on the `variant`: the `split` variant uses a
   * tighter `spacing[2.5]` (40px) block padding, while the `stacked` variant
   * uses `spacing[4]` (64px).
   *
   * default: false
   */
  isPadded?: boolean;
  /**
   * Optional prop primarily used for extending styles
   */
  className?: string;
  /**
   * Optional `id` prop for anchoring directly to the section
   */
  id?: string;
  /**
   * Which gradient should be applied?
   *
   * NOTE: We recommend choosing a colorScheme that has
   * a similar look as the gradient to avoid and weird
   * box-shadows on things like ImageCards
   */
  gradient?: Gradient;
  /**
   * Sets the optional background image source. This should be an abolute path
   * to an image file. Preferably a jpg to keep the image file as small as possible.
   */
  backgroundImageSrc?: string;
  /**
   * The variant of the section, either 'split' or 'stacked', used to determine the layout of the section modules. Defaults to 'stacked'.
   */
  variant?: 'split' | 'stacked';
};

type SectionWithChildren = BaseSectionProps & {
  /**
   * Modular content as children
   */
  children: ReactNode;
  contentModules?: never;
  railModules?: never;
};

type SectionWithModules = BaseSectionProps & {
  children?: never;
  /**
   * Content modules to be rendered in the main content area of the section.
   *
   * When used with `railModules`, on desktop this creates a split layout where
   * the content & rail appear side by side. The content modules appear in the
   * larger column, while the rail modules appear in the smaller column
   * (sticky).
   *
   * For SEO purposes and small device UX, the content modules will stack below
   * the rail modules.
   */
  contentModules: ReactNode;
  /**
   * Rail modules to be rendered in the sidebar content area of the section.
   *
   * When used with `contentModules`, on desktop this creates a split layout
   * where the content & rail appear side by side. The content modules appear
   * in the larger column, while the rail modules appear in the smaller column
   * (sticky).
   *
   * For SEO purposes and small device UX, the content modules will stack below
   * the rail modules.
   */
  railModules: ReactNode;
};

type SectionProps = SectionWithChildren | SectionWithModules;

// All :root overrides for an in-view Section live in a single createGlobalStyle.
// Splitting them across sibling instances triggers a styled-components v6 issue
// where only one instance's rule is inserted when multiple are mounted together.
const RootStyles = createGlobalStyle<{ cs?; gradient?; imgSrc? }>`
 ${({ cs, gradient, imgSrc, theme }) =>
   `
  :root {
    ${
      cs
        ? `
    --global-color: ${theme.colorSchemes[cs].fg};
    --global-inactive-color: ${theme.colorSchemes[cs].inactive};
    --global-link-color: ${theme.colorSchemes[cs].link};
    --global-hover-color: ${theme.colorSchemes[cs].hover};
    --global-bg-color: ${theme.colorSchemes[cs].bg};
    --global-secondary-bg-color: ${theme.colorSchemes[cs].secondaryBg};
    --global-visited-color: ${theme.colorSchemes[cs].visited};
    `
        : ''
    }
    ${
      gradient
        ? `
    --gradient-base-ne: ${theme.gradients[gradient].ne};
    --gradient-base-se: ${theme.gradients[gradient].se};
    --gradient-base-sw: ${theme.gradients[gradient].sw};
    --gradient-base-nw: ${theme.gradients[gradient].nw};
    `
        : ''
    }
    ${imgSrc ? `--global-bg-image: url(${imgSrc});` : ''}
  }
 `}
`;

const SectionWrapper = styled.section`
  &.padded {
    padding-block: ${({ theme }) => theme.spacing[4]};
  }
  &.padded.split {
    padding-block: ${({ theme }) => theme.spacing[2.5]};
  }
`;

const SectionContent = styled.div`
  margin-inline: auto;
  max-width: ${({ theme }) => theme.breakpoints.xl};
  padding-inline: ${({ theme }) => theme.grid.margin.xsmall};
  ${({ theme }) => theme.mq.minWidth.small} {
    padding-inline: ${({ theme }) => theme.grid.margin.small};
  }
  ${({ theme }) => theme.mq.minWidth.medium} {
    padding-inline: ${({ theme }) => theme.grid.margin.medium};
  }
  /* large and xl are the same */
  ${({ theme }) => theme.mq.minWidth.large} {
    padding-inline: ${({ theme }) => theme.grid.margin.large};
  }

  /* split variant introduces two columns at 1024px */
  &.split {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};

    ${({ theme }) => theme.mq.minWidth.large} {
      display: grid;
      ${({ theme }) => theme.grid.columns.large}

      .content {
        grid-column: 5 / span 8;
      }
      .rail {
        grid-column: 1 / span 3;
        align-self: start;
        position: sticky;
        // top math is set to allow enough space for the sticky main navigation
        // as well as the padding in the section
        top: ${({ theme }) => theme.spacing[5]};
      }
    }
  }
`;

// This is similar to what we do in ContentfulImage
const setOptimizedUrl = (src) => {
  src = resolveAssetReference(src);
  if (imageRendition(src)) return ownedImageLoader({ src, width: 2048 });
  if (src.startsWith('/') || isHostedAsset(src)) return src;
  const url = new URL(src);
  url.searchParams.set('fm', 'avif');
  url.searchParams.set('fit', 'fill');
  url.searchParams.set('w', '2160'); // Max of DVDs grid * 1.5
  url.searchParams.set('q', '70');
  return url.href;
};

/**
 * Primary sectioning component for a page providing content bounds for the
 * width **and** the page-level color scheme.
 *
 * Each page should have __at least one__ Section component.
 *
 * ## Variants
 *
 * **Stacked (default)**: Content flows in a single column layout. Perfect for
 * most existing DVD modules, especially when you want a clean, linear reading
 * experience.
 *
 * **Split**: Creates a two-column layout on desktop (1024px+) with a main
 * content area and a sticky sidebar rail. The rail content appears above the
 * main content on mobile for better UX. Ideal for pages with supporting
 * content like integration pages with profile cards, or pages that benefit
 * from a persistent sidebar.
 *
 * ## Best Practices
 *
 * - Use `isPadded={true}` for split variants at the top of pages to provide
 *   breathing room below navigation
 * - Keep rail content concise and actionable - it should complement, not
 *   compete with, the main content
 * - Consider mobile-first: rail content stacks above main content on small
 *   screens, so ensure it makes sense in that order
 * - Use `contentModules` and `railModules` props when using the split variant
 *   instead of `children` for module placement in the layout
 */
const Section = ({
  isPadded = false,
  colorScheme,
  children,
  contentModules = null,
  railModules = null,
  className,
  id,
  gradient = null,
  backgroundImageSrc = null,
  variant = 'stacked',
}: SectionProps) => {
  const { ref, inView } = useInView({
    // Creates a horizontal line halfway from the bottom of the viewport that
    // will trigger an intersection at that the very point
    rootMargin: '-50% 0% -50% 0%',
  });

  const optimizedUrl = backgroundImageSrc
    ? setOptimizedUrl(backgroundImageSrc)
    : null;

  return (
    <SectionWrapper
      ref={ref}
      className={cs(className, {
        padded: isPadded,
        split: variant === 'split',
      })}
      id={id}
    >
      {inView && (colorScheme || gradient || backgroundImageSrc) ? (
        <RootStyles
          cs={colorScheme}
          gradient={gradient}
          imgSrc={optimizedUrl}
        />
      ) : null}
      <SectionContent className={cs(variant)}>
        {contentModules ? (
          <>
            {railModules && <div className="rail">{railModules}</div>}
            <div className="content">{contentModules}</div>
          </>
        ) : (
          children
        )}
      </SectionContent>
    </SectionWrapper>
  );
};

export default Section;
